import type { ReactNode } from 'react';
import { Badge, Group, Progress, Stack, Text } from '@mantine/core';
import { percent as toPercent } from '@/lib/forwardingView';
import { formatMoney } from '@/lib/format';

interface StageSectionProps {
  readonly title: string;
  readonly unit: string;
  readonly doneLabel: string;
  readonly done: number | undefined;
  readonly total?: number | undefined;
  readonly remainingLabel?: string;
  readonly remaining?: number | undefined;
  readonly count?: number;
  readonly countLabel?: string;
  readonly children?: ReactNode;
  readonly emptyText?: string;
  /** Компактный режим (внутри таба): тонкий recap «сделано · осталось · ТС» без заголовка/большого бара. */
  readonly compact?: boolean;
}

// Этап перевозки: заголовок (+кол-во ТС), прогресс-бар, строка «сделано / осталось», детали (children).
// В compact-режиме (таб этапа) большая сводка убрана — она в дашборде-воронке; остаётся тонкий recap.
export default function StageSection({
  title,
  unit,
  doneLabel,
  done,
  total,
  remainingLabel,
  remaining,
  count,
  countLabel,
  children,
  emptyText,
  compact,
}: StageSectionProps) {
  return (
    <Stack gap="xs">
      {!compact ? (
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <Text fz="sm" fw={600} c="gray.8">
              {title}
            </Text>
            {count != null && count > 0 ? (
              <Badge variant="light" color="gray" radius="sm" tt="none" fw={500}>
                {count} {countLabel}
              </Badge>
            ) : null}
          </Group>
          <Text fz="sm" fw={600}>
            {formatMoney(done)} {unit}
          </Text>
        </Group>
      ) : null}

      {!compact && total != null && total > 0 ? (
        <Stack gap={2}>
          <Progress value={toPercent(done, total)} size="sm" radius="xl" color="brand" />
          <Group justify="space-between" wrap="nowrap">
            <Text fz={10} c="gray.5">
              0 {unit}
            </Text>
            <Text fz={10} c="gray.5">
              {formatMoney(total)} {unit}
            </Text>
          </Group>
        </Stack>
      ) : null}

      <Group gap="lg" wrap="wrap">
        <Text fz="xs" c="gray.7">
          {doneLabel}:{' '}
          <Text span fz="xs" c="gray.8" fw={500}>
            {formatMoney(done)} {unit}
          </Text>
        </Text>
        {remainingLabel && remaining != null ? (
          <Text fz="xs" c="gray.7">
            {remainingLabel}:{' '}
            <Text span fz="xs" c="gray.8" fw={500}>
              {formatMoney(remaining)} {unit}
            </Text>
          </Text>
        ) : null}
        {compact && count != null && count > 0 ? (
          <Text fz="xs" c="gray.7">
            {count} {countLabel}
          </Text>
        ) : null}
      </Group>

      {emptyText ? (
        <Text fz="sm" c="gray.6">
          {emptyText}
        </Text>
      ) : (
        children
      )}
    </Stack>
  );
}
